import { useEffect, useRef } from "react";

/**
 * Run something when the creator switches Instagram accounts.
 *
 * ═══════════════════════════════════════════════════════════════════
 *  WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════
 * The sidebar switcher has dispatched `ig-active-account-switched` since
 * it was built. Exactly ONE page listened — the inbox. Every other
 * account-scoped surface kept whatever it had already fetched, so
 * switching accounts changed a pointer on the server and left the screen
 * showing the previous account's data with nothing admitting it.
 *
 * That was invisible while nobody owned two accounts. Pro allows three.
 *
 * ═══════════════════════════════════════════════════════════════════
 *  WHY THE CALLBACK GOES THROUGH A REF
 * ═══════════════════════════════════════════════════════════════════
 * The obvious `useEffect(..., [onSwitch])` re-subscribes on every render
 * unless the caller wraps their handler in useCallback — and the one
 * that forgets gets a listener rebound on each keystroke. The obvious
 * fix, `[]` deps, captures the FIRST render's closure and calls a
 * refetch that reads stale filters.
 *
 * InboxPage hit this and worked around it with its own `syncNowRef`.
 * The ref is that workaround, made the default: subscribe once, always
 * call the latest handler.
 */
export function useActiveAccountSwitch(onSwitch) {
  const handler = useRef(onSwitch);
  handler.current = onSwitch;

  useEffect(() => {
    const listener = (event) => {
      handler.current?.(event?.detail?.accountId);
    };
    window.addEventListener("ig-active-account-switched", listener);
    return () =>
      window.removeEventListener("ig-active-account-switched", listener);
  }, []);
}

export default useActiveAccountSwitch;
